'use client'

import ClientOnly from '@/components/client-only/ClientOnly'
import { formatTimeToParts, getTimeToNow, padZero } from '@/utils/time'
import { forwardRef, useEffect, useMemo, useState } from 'react'

type Format =
  | 'hh:mm:ss'
  | 'dd:hh:mm:ss'
  | 'hh:mm'
  | 'dd:hh:mm'
  | 'mm:ss'
  | 'hh'
  | 'dd'
  | 'mm'
  | 'ss'

export interface CountdownProps extends React.HTMLAttributes<HTMLSpanElement> {
  date: string | number | Date
  format?: Format
}

const Countdown = forwardRef(function Countdown(
  props: CountdownProps,
  ref: React.ForwardedRef<HTMLSpanElement>
) {
  const { className, date = Date.now(), format = 'hh:mm:ss', ...other } = props
  
  return (
    <ClientOnly>
      <CountdownContent 
        className={className} 
        date={date} 
        format={format} 
        ref={ref} 
        {...other} 
      />
    </ClientOnly>
  )
})

interface CountdownContentProps extends CountdownProps {
  ref: React.ForwardedRef<HTMLSpanElement>
}

function CountdownContent({ 
  className, 
  date = Date.now(), 
  format = 'hh:mm:ss', 
  ref,
  ...other 
}: CountdownContentProps) {
  const [timeLeft, setTimeLeft] = useState(getTimeToNow(date))

  useEffect(() => {
    let timeoutId: number

    const updateTime = () => {
      const diff = getTimeToNow(date)

      if (diff <= 0) {
        setTimeLeft(0)
      } else {
        setTimeLeft(diff)
        timeoutId = window.setTimeout(updateTime, diff % 1000)
      }
    }

    updateTime()

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [date])

  const [showDays, showHours, showMinutes, showSeconds] = useMemo(() => {
    let showDays = false
    let showHours = false
    let showMinutes = false
    let showSeconds = false

    switch (format) {
      case 'dd:hh:mm:ss':
        showDays = true
        showHours = true
        showMinutes = true
        showSeconds = true
        break
      case 'hh:mm:ss':
        showHours = true
        showMinutes = true
        showSeconds = true
        break
      case 'dd:hh:mm':
        showDays = true
        showHours = true
        showMinutes = true
        break
      case 'hh:mm':
        showHours = true
        showMinutes = true
        break
      case 'mm:ss':
        showMinutes = true
        showSeconds = true
        break
      case 'hh':
        showHours = true
        break
      case 'dd':
        showDays = true
        break
      case 'mm':
        showMinutes = true
        break
      case 'ss':
        showSeconds = true
        break
    }

    return [showDays, showHours, showMinutes, showSeconds]
  }, [format])

  const parts = formatTimeToParts(timeLeft, {
    days: showDays,
    hours: showHours,
    minutes: showMinutes,
    seconds: showSeconds,
  })

  return (
    <span ref={ref} role='timer' {...other}>
      {parts.map(({ value }) => padZero(value)).join(':')}
    </span>
  )
}

export default Countdown
